import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { dataApi } from '../services/api';

/**
 * Licence & entitlements
 *
 * What the current organisation is on, and how much of its monthly AI
 * allowance it has left. Read directly from `org_licences` and `plans` — the
 * licence row grants read to its own team and write to nobody, so this is a
 * point read rather than a function call. That matters: the sidebar meter and
 * the Generate button both depend on it, and neither should wait on a cold
 * start.
 *
 * This is a CONVENIENCE, not a control. Everything here is display state. The
 * actual entitlement decision happens server-side in the assessment
 * orchestrator against the same row, so a user who edits this state in devtools
 * changes what they see and nothing about what they can do.
 *
 * Failure is deliberately permissive: if the summary can't be loaded we return
 * a "no limits known" state rather than blocking. An outage in billing
 * plumbing must never stop a shop quoting a car — the server-side check is
 * still there to catch genuine over-use.
 */

const LicenceContext = createContext(null);

/** Shape used before the first load completes, and if loading fails. */
const UNKNOWN = {
  planId: null,
  planName: null,
  status: null,
  usage: null,
  plan: null,
  catalogue: [],
  isAdmin: false,
};

export function LicenceProvider({ children }) {
  const { isAuthenticated, user } = useAuth();

  const [licence, setLicence] = useState(UNKNOWN);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const teamId = user?.teamId || null;

  const refresh = useCallback(async () => {
    if (!teamId) {
      setLicence(UNKNOWN);
      setLoading(false);
      return null;
    }

    try {
      setError(null);
      let summary = await dataApi.licenceSummary();

      // No licence row: an organisation created before licensing existed, or
      // straight from the Appwrite console. Ask the server to backfill a free
      // one (the only write path into that table) and read it back.
      if (!summary) {
        console.info('No licence for this organisation — provisioning the free plan');
        await dataApi.licenceProvision();
        summary = await dataApi.licenceSummary();
      }

      setLicence(summary || UNKNOWN);
      return summary;
    } catch (err) {
      // Log it, don't surface it. The user didn't ask about their licence —
      // they asked to quote a car, and they should still be able to.
      console.warn('Could not load licence summary:', err.message);
      setError(err.message);
      setLicence(UNKNOWN);
      return null;
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (!isAuthenticated || !teamId) {
      setLicence(UNKNOWN);
      setLoading(false);
      return;
    }
    setLoading(true);
    refresh();
  }, [isAuthenticated, teamId, refresh]);

  const usage = licence?.usage || null;

  /**
   * Whether to show this person the billing controls. Mirrors the rule the
   * `licence` function enforces — Appwrite team owner, or the `admin` label —
   * but only decides what's rendered. The function re-checks it for real on
   * every billing route.
   */
  const isAdmin = !!user?.isTeamOwner || (Array.isArray(user?.labels) && user.labels.includes('admin'));

  /**
   * Can this org run another AI assessment right now?
   *
   * Unknown counts as yes. If we never managed to load the summary we let the
   * attempt through and let the server be the one to say no — that keeps a
   * billing hiccup from looking like a broken app.
   */
  const canRunAiAssessment = !usage || usage.unlimited || !usage.exhausted;

  /**
   * Is there allowance for another AI assessment?
   *
   * Every run costs a credit, including a re-run over a quote request that has
   * already been assessed — it's another use of the models either way, and
   * pinning the charge to the quote request would let anyone get unlimited
   * assessments by swapping the photos on one request and re-running it.
   */
  const checkAiAssessment = useCallback(
    () => {
      if (!usage) return { allowed: true, reason: 'unknown' };
      if (usage.unlimited) return { allowed: true, reason: 'unlimited' };
      if (!usage.exhausted) return { allowed: true, remaining: usage.remaining };
      return {
        allowed: false,
        reason: 'limit-reached',
        used: usage.used,
        limit: usage.limit,
        resetsAt: usage.resetsAt,
      };
    },
    [usage]
  );

  const value = useMemo(
    () => ({
      licence,
      loading,
      error,
      refresh,

      planId: licence?.planId || null,
      planName: licence?.planName || null,
      status: licence?.status || null,
      isFreePlan: licence?.planId === 'free',
      isAdmin,
      catalogue: licence?.catalogue || [],

      usage,
      canRunAiAssessment,
      checkAiAssessment,
    }),
    [licence, loading, error, refresh, usage, isAdmin, canRunAiAssessment, checkAiAssessment]
  );

  return <LicenceContext.Provider value={value}>{children}</LicenceContext.Provider>;
}

export function useLicence() {
  const ctx = useContext(LicenceContext);
  if (!ctx) {
    throw new Error('useLicence must be used within a LicenceProvider');
  }
  return ctx;
}
